/**
 * "Versões do app" (ANLT-06 AC 7): the highest installed version and the
 * bars of the 2 versions with most users plus "Outras", from
 * `usersByFullVersion`.
 */

export interface VersionRow {
  versionCode: string;
  userCount: number;
}

export type VersionBar =
  | { kind: 'version'; label: string; users: number; pct: number }
  | { kind: 'others'; users: number; pct: number };

export interface VersionsSummary {
  latest: { version: string; pct: number } | null;
  bars: VersionBar[];
}

const TOP_VERSIONS = 2;

/** Numeric parts of `1.21.0`; `null` for anything else (e.g. `unknown`). */
function parseVersion(code: string): number[] | null {
  return /^\d+(\.\d+)*$/.test(code) ? code.split('.').map(Number) : null;
}

function compareVersions(a: number[], b: number[]): number {
  for (let i = 0; i < Math.max(a.length, b.length); i++) {
    const diff = (a[i] ?? 0) - (b[i] ?? 0);
    if (diff !== 0) return diff;
  }
  return 0;
}

/**
 * `pct` in 0–100 of every user in `rows` (unknown versions included). Users
 * without a known version only count in the total and in "Outras". `null`
 * without users.
 */
// SPEC_DEVIATION: bars are `{ kind: 'version' | 'others' }` (design:
// `{ label; users; pct }`). Reason: "Outras" is translated by the component.
export function summarizeVersions(rows: VersionRow[]): VersionsSummary | null {
  const total = rows.reduce((sum, r) => sum + r.userCount, 0);
  if (total === 0) return null;
  const pct = (users: number) => (users / total) * 100;

  const versions = rows
    .map((r) => ({ ...r, parts: parseVersion(r.versionCode) }))
    .filter(
      (r): r is VersionRow & { parts: number[] } =>
        r.parts !== null && r.userCount > 0
    );

  const byVersionDesc = [...versions].sort((a, b) =>
    compareVersions(b.parts, a.parts)
  );
  const latest = byVersionDesc[0];

  const top = [...byVersionDesc]
    .sort((a, b) => b.userCount - a.userCount)
    .slice(0, TOP_VERSIONS);
  const bars: VersionBar[] = top.map((r) => ({
    kind: 'version',
    label: r.versionCode,
    users: r.userCount,
    pct: pct(r.userCount),
  }));
  const rest = total - top.reduce((sum, r) => sum + r.userCount, 0);
  if (rest > 0) bars.push({ kind: 'others', users: rest, pct: pct(rest) });

  return {
    latest: latest
      ? { version: latest.versionCode, pct: pct(latest.userCount) }
      : null,
    bars,
  };
}
